import { useQuery } from "@tanstack/react-query";

import Sentry from "@similarweb/sentry-logger";

import type { ICountryTrafficShare } from "app/pages/website-performance/components/TopCountries/type";
import { DefaultFetchService } from "app/services/fetchService";

const ENDPOINT = "widgetApi/WebsiteGeography/Geography/Table";

export const TOP_COUNTRIES_PAGE_SIZE = 5;

interface ICountryTrafficShareData {
    Country: number;
    Share: number;
    Change: number | null;
    ShareList: Record<string, number>;
}

export interface ITopCountriesParams {
    keys: string;
    webSource: string;
    includeSubDomains: boolean;
    from: string;
    to: string;
    isWindow: boolean | undefined;
}

/**
 * Top countries by traffic share, always worldwide regardless of the page's country filter.
 *
 * `change` is the month-over-month visit change the backend computes per country, as a signed
 * fraction; it is null for 28-day windows, which have no monthly baseline. Shared by the geography
 * widget and the Insight Ribbon so both read one cached Firebolt query.
 */
export const useTopCountriesData = (params: ITopCountriesParams, { enabled = true }: { enabled?: boolean } = {}) => {
    // Normalized so callers that leave isWindow undefined still share one cache entry with those that pass false.
    const request = { ...params, isWindow: !!params.isWindow };

    return useQuery<ICountryTrafficShare[]>({
        queryKey: ["websitePerformanceTopCountries", request],
        queryFn: async () => {
            try {
                const { Data } = await DefaultFetchService.getInstance().get<{ Data: ICountryTrafficShareData[] }>(
                    ENDPOINT,
                    {
                        country: 999,
                        includeSubDomains: request.includeSubDomains,
                        webSource: request.webSource,
                        timeGranularity: request.isWindow ? "Daily" : "Monthly",
                        orderBy: "TotalShare desc",
                        keys: request.keys,
                        pageSize: TOP_COUNTRIES_PAGE_SIZE,
                        from: request.from,
                        to: request.to,
                        isWindow: request.isWindow,
                    },
                );

                return Data.map((country) => ({
                    country: country.Country,
                    share: country.Share,
                    shareList: country.ShareList,
                    change: country.Change,
                }));
            } catch (error) {
                Sentry.errorTracker({
                    e: error instanceof Error ? error : new Error(String(error)),
                    message: "Insight Ribbon failed to load geography",
                    level: "error",
                    params: { extra: { key: request.keys } },
                });
                throw error;
            }
        },
        enabled: enabled && !!request.keys,
    });
};
